import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumbs from '@/components/feature/Breadcrumbs';
import ScrollToTop from '@/components/feature/ScrollToTop';
import SEOHead from '@/components/feature/SEOHead';
import { articles } from '@/mocks/articles';
import { categories } from '@/mocks/categories';
import NotFound from '@/pages/NotFound';

export default function ArticleDetail() {
  const { slug } = useParams<{ slug: string }>();

  const article = articles.find((a) => a.slug === slug);

  if (!article) {
    return <NotFound />;
  }

  const category = categories.find((c) => c.id === article.categoryId);
  const relatedArticles = articles
    .filter((a) => a.categoryId === article.categoryId && a.id !== article.id)
    .slice(0, 3);

  return (
    <>
      <ScrollToTop />
      <SEOHead
        title={`${article.title} - 开云平台`}
        description={article.excerpt}
        keywords={`开云${article.title},开云${category?.name || ''}`}
        canonicalUrl={`https://kaiyun.example.com/articles/${article.slug}`}
        ogType="article"
      />

      {/* Article Schema.org */}
      <script type="application/ld+json">
        {JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'Article',
          headline: article.title,
          description: article.excerpt,
          author: { '@type': 'Person', name: article.author },
          datePublished: article.date,
          image: article.image,
          publisher: { '@type': 'Organization', name: '开云' },
        })}
      </script>

      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-20">
          <div className="max-w-7xl mx-auto px-4 md:px-6 py-8">
            <Breadcrumbs
              items={[
                { label: '首页', path: '/' },
                { label: '文章中心', path: '/articles' },
                ...(category ? [{ label: category.name, path: `/categories/${category.slug}` }] : []),
                { label: article.title },
              ]}
            />
          </div>

          <article className="pb-16">
            <div className="max-w-4xl mx-auto px-4 md:px-6">
              <div className="mb-8">
                {category && (
                  <Link
                    to={`/categories/${category.slug}`}
                    className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-accent-100 text-accent-700 text-xs font-medium mb-4 hover:bg-accent-200 transition-colors"
                  >
                    <i className={`${category.icon} text-sm`}></i>
                    {category.name}
                  </Link>
                )}
                <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-4">{article.title}</h1>
                <div className="flex items-center gap-3 text-sm text-foreground-500">
                  <span>{article.author}</span>
                  <span>·</span>
                  <span>{article.date}</span>
                </div>
              </div>

              <div className="rounded-xl overflow-hidden mb-10">
                <img
                  src={article.image}
                  alt={article.title}
                  className="w-full h-[400px] object-cover object-top"
                />
              </div>

              <div
                className="article-content max-w-none"
                dangerouslySetInnerHTML={{ __html: article.content }}
              />

              {relatedArticles.length > 0 && (
                <div className="mt-16 pt-10 border-t border-background-200/70">
                  <h3 className="text-lg font-heading font-semibold text-foreground-900 mb-6">相关文章</h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                    {relatedArticles.map((ra) => (
                      <Link
                        key={ra.id}
                        to={`/articles/${ra.slug}`}
                        className="group rounded-lg bg-background-100 border border-background-200/70 overflow-hidden hover:border-primary-200 transition-all duration-300"
                      >
                        <div className="h-36 overflow-hidden">
                          <img src={ra.image} alt={ra.title} className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500" />
                        </div>
                        <div className="p-4">
                          <h4 className="text-sm font-heading font-semibold text-foreground-900 group-hover:text-primary-600 transition-colors line-clamp-2">
                            {ra.title}
                          </h4>
                        </div>
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </article>
        </main>
        <Footer />
      </div>
    </>
  );
}